import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { deepCopy, deepEquals } from "app/tools/common"
import { getDateWithMaxTime, getDateWithMinTime } from "app/util/date/tools/common"
import { messageSVC } from "message/service"
import { ICreateUserReportParams, IReportParameters, IUpdateUserReportParams, IUserReport } from "reports/model"
import { reportsRPO } from "reports/repository"
import { state } from "reports/state"
import { reportsActionCreator } from "reports/store/actions"
import { getDefaultReportParameters, getReportFormState } from "reports/store/state"
import { getStartEndFilters } from "ui/model"
import { getReportForm } from "./ui"

export const getReportParametersFromUserReport = (userReport: IUserReport): IReportParameters => ({
  filters: userReport.filters || {},
  groupBys: userReport.groupBys,
  sortBys: userReport.sortBys || [],
  name: userReport.name,
  description: userReport.description
})

export const loadMyReports = async (): Promise<void> => {
  try {
    if (getStore().getState().reports.isListLoading) {
      return
    }
    getStore().dispatch(reportsActionCreator.setIsListLoading(true))
    const myReports = await reportsRPO.getMyUserReports()
    getStore().dispatch(reportsActionCreator.setReportsList(myReports))
  } catch (error) {
    console.error(error)
    getStore().dispatch(reportsActionCreator.setListHasLoadingError(true))
  }
}

export const hasReportSearchChanged = (
  parameters: IReportParameters,
  appliedParameters: IReportParameters | null
): boolean => {
  if (appliedParameters === null) {
    return !deepEquals(parameters, getDefaultReportParameters())
  }
  return !deepEquals(parameters, appliedParameters)
}

export const setReportResultIsLoading = (isCreation: boolean, isLoading: boolean) => {
  if (getReportForm(isCreation).isResultLoading === isLoading) {
    return
  }
  getStore().dispatch(reportsActionCreator.setIsResultLoading(isCreation, isLoading))
}

export const setReportResultHasLoadingError = (isCreation: boolean, hasLoadingError: boolean) => {
  getStore().dispatch(reportsActionCreator.setHasLoadingError(isCreation, hasLoadingError))
}

export const clearReportsParameters = (isCreation: boolean) => {
  if (isCreation) {
    setReportsParameters(true, getDefaultReportParameters())
  } else {
    setReportsParameters(false, getReportFormState(false, getReportForm(false).report as IUserReport).parameters)
  }
}

export const setReportsParameters = (isCreation: boolean, parameters: Partial<IReportParameters>) => {
  const newParameters = { ...getReportForm(isCreation).parameters, ...parameters }
  let updateFormError = validateReportForm(isCreation, newParameters)
  const isResultLoading =
    (parameters.filters !== undefined || parameters.groupBys !== undefined || parameters.sortBys !== undefined) &&
    newParameters.groupBys.length > 0
  getStore().dispatch(reportsActionCreator.setParameters(isCreation, newParameters, updateFormError, isResultLoading))
  if (!isResultLoading) {
    return
  }
  if (newParameters.groupBys.length > 0) {
    clearTimeout(state.applyTimeout)
    state.applyTimeout = setTimeout(() => applyReportsParameters(isCreation, newParameters), 650)
  }
}

export const applyReportsParameters = (isCreation: boolean, parameters: Partial<IReportParameters>): Promise<void> =>
  new Promise<void>(async resolve => {
    const newParameters = { ...getReportForm(isCreation).parameters, ...parameters }
    const toProcessParameters = deepCopy(newParameters)
    try {
      if (toProcessParameters.filters.startDate) {
        const allFilters = getStartEndFilters()
        const activeFilter = allFilters.find(f => f.labelKey === toProcessParameters.filters.startDate)
        if (activeFilter) {
          if (activeFilter.startDate) {
            toProcessParameters.filters.startDate = activeFilter.startDate.toISOString()
          }
          if (activeFilter.endDate) {
            toProcessParameters.filters.endDate = activeFilter.endDate.toISOString()
          }
        }
      }
      if (toProcessParameters.filters.startDate) {
        toProcessParameters.filters.startDate = getDateWithMinTime(
          new Date(toProcessParameters.filters.startDate)
        ).toISOString()
      }
      if (toProcessParameters.filters.endDate) {
        toProcessParameters.filters.endDate = getDateWithMaxTime(
          new Date(toProcessParameters.filters.endDate)
        ).toISOString()
      }
      const currentLaunchRequestId = ++state.currentLaunchRequestId
      const results = await reportsRPO.getResults(toProcessParameters)
      if (currentLaunchRequestId !== state.currentLaunchRequestId) {
        return
      }
      getStore().dispatch(reportsActionCreator.setResults(isCreation, results.data, newParameters))
    } catch (error) {
      console.error(error)
      setReportResultHasLoadingError(isCreation, true)
    }
    resolve()
  })

export const loadReportForm = async (reportId: number) => {
  const reportState = getStore().getState().reports
  const allReports = reportState.userReports
  if (!allReports) {
    if (reportState.isListLoading) {
      setTimeout(() => loadReportForm(reportId), 500)
      return
    }
    await loadMyReports()
    loadReportForm(reportId)
    return
  }
  const report = allReports.find(r => r.id === reportId)
  if (!report) {
    console.error(`Unable to find report id="${reportId}"`)
    return
  }
  getStore().dispatch(reportsActionCreator.setEditForm(getReportFormState(false, report)))
  applyReportsParameters(false, report)
}

export const createUserReport = async (params: ICreateUserReportParams): Promise<IUserReport | undefined> => {
  try {
    const userReport = await reportsRPO.createUserReport(params)
    getStore().dispatch(reportsActionCreator.addUserReport(userReport))
    return userReport
  } catch (error) {
    console.error(error)
    messageSVC.error(translate("reports.add.error"))
  }
}

export const updateUserReport = async (params: IUpdateUserReportParams): Promise<IUserReport | undefined> => {
  try {
    getStore().dispatch(reportsActionCreator.setFormIsUpdating(true))
    const userReport = await reportsRPO.updateUserReport(params)
    getStore().dispatch(reportsActionCreator.updateUserReport(userReport))
    return userReport
  } catch (error) {
    console.error(error)
    getStore().dispatch(reportsActionCreator.setFormIsUpdating(false))
    messageSVC.error(translate("reports.update.error"))
  }
}

export const deleteUserReport = async (userReportId: number): Promise<IUserReport | undefined> => {
  try {
    const userReport = await reportsRPO.delUserReport(userReportId)
    getStore().dispatch(reportsActionCreator.deletUserReport(userReportId))
    return userReport
  } catch (error) {
    console.error(error)
    messageSVC.error(translate("reports.delete.error"))
  }
}

const validateReportForm = (isCreation: boolean, parameters: IReportParameters): string => {
  if (isCreation) {
    return ""
  }
  if (!parameters.name) {
    return translate("reports.error.required")
  } else if (parameters.name.length > 256) {
    return translate("reports.error.name.too.long")
  }
  if (parameters.description && parameters.description.length > 256) {
    return translate("reports.error.description.too.long")
  }
  return ""
}
